import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Send, MessageCircle, ArrowLeft } from "lucide-react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { useState } from "react";
import { formatDistanceToNow } from "date-fns";

interface DirectMessagingProps {
  partnerId?: string;
}

export function DirectMessaging({ partnerId }: DirectMessagingProps) {
  const queryClient = useQueryClient();
  const [selectedPartner, setSelectedPartner] = useState<string | null>(partnerId || null);
  const [message, setMessage] = useState("");

  const { data: user } = useQuery({
    queryKey: ['user'],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      return user;
    },
  });

  const { data: partners } = useQuery({
    queryKey: ['partners', user?.id],
    queryFn: async () => {
      if (!user?.id) return [];

      const { data, error } = await supabase
        .from('accountability_partnerships')
        .select('*')
        .or(`user_id.eq.${user.id},partner_id.eq.${user.id}`)
        .eq('status', 'active');

      if (error) throw error;

      const partnerIds = data.map(p => p.user_id === user.id ? p.partner_id : p.user_id);
      
      if (partnerIds.length === 0) return [];

      const { data: profiles } = await supabase
        .from('profiles')
        .select('user_id, display_name')
        .in('user_id', partnerIds);

      // Get unread counts
      const { data: unreadMessages } = await supabase
        .from('direct_messages')
        .select('sender_id')
        .eq('receiver_id', user.id)
        .eq('is_read', false);

      const unreadCounts = unreadMessages?.reduce((acc, msg) => {
        acc[msg.sender_id] = (acc[msg.sender_id] || 0) + 1;
        return acc;
      }, {} as Record<string, number>) || {};

      return profiles?.map(p => ({
        ...p,
        unread_count: unreadCounts[p.user_id] || 0,
      })) || [];
    },
    enabled: !!user?.id,
  });

  const { data: messages } = useQuery({
    queryKey: ['direct-messages', selectedPartner, user?.id],
    queryFn: async () => {
      if (!user?.id || !selectedPartner) return [];

      const { data, error } = await supabase
        .from('direct_messages')
        .select('*')
        .or(`and(sender_id.eq.${user.id},receiver_id.eq.${selectedPartner}),and(sender_id.eq.${selectedPartner},receiver_id.eq.${user.id})`)
        .order('created_at', { ascending: true });

      if (error) throw error;

      // Mark messages as read
      await supabase
        .from('direct_messages')
        .update({ is_read: true })
        .eq('receiver_id', user.id)
        .eq('sender_id', selectedPartner)
        .eq('is_read', false);

      return data;
    },
    enabled: !!user?.id && !!selectedPartner,
  });

  const sendMessageMutation = useMutation({
    mutationFn: async () => {
      if (!user?.id || !selectedPartner || !message.trim()) return;

      const { error } = await supabase
        .from('direct_messages')
        .insert({
          sender_id: user.id,
          receiver_id: selectedPartner,
          message: message.trim(),
        });

      if (error) throw error;

      // Create notification
      await supabase
        .from('notifications')
        .insert({
          user_id: selectedPartner,
          type: 'direct_message',
          title: 'New Message',
          message: `You have a new message from your accountability partner`,
          link: '/connect-and-grow?tab=partners',
        });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['direct-messages'] });
      queryClient.invalidateQueries({ queryKey: ['partners'] });
      setMessage("");
    },
    onError: () => {
      toast.error("Failed to send message");
    },
  });

  const selectedPartnerProfile = partners?.find(p => p.user_id === selectedPartner);

  if (!selectedPartner) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <MessageCircle className="h-5 w-5 text-accent" />
            Messages
          </CardTitle>
        </CardHeader>
        <CardContent>
          {partners && partners.length > 0 ? (
            <div className="space-y-2">
              {partners.map((partner) => (
                <Button
                  key={partner.user_id}
                  variant="outline"
                  className="w-full justify-between h-auto py-3"
                  onClick={() => setSelectedPartner(partner.user_id)}
                >
                  <div className="flex items-center gap-3">
                    <Avatar>
                      <AvatarFallback>{partner.display_name?.[0] || 'U'}</AvatarFallback>
                    </Avatar>
                    <span>{partner.display_name || 'User'}</span>
                  </div>
                  {partner.unread_count > 0 && (
                    <Badge variant="destructive">{partner.unread_count}</Badge>
                  )}
                </Button>
              ))}
            </div>
          ) : (
            <div className="text-center py-8">
              <MessageCircle className="h-12 w-12 mx-auto text-muted-foreground/50 mb-3" />
              <p className="text-sm text-muted-foreground">
                No partners yet. Find accountability partners to start messaging!
              </p>
            </div>
          )}
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="flex flex-col h-[500px] sm:h-[600px]">
      <CardHeader className="border-b p-3 sm:p-6">
        <div className="flex items-center gap-2 sm:gap-3">
          <Button variant="ghost" size="icon" onClick={() => setSelectedPartner(null)} className="h-8 w-8 sm:h-10 sm:w-10">
            <ArrowLeft className="h-4 w-4 sm:h-5 sm:w-5" />
          </Button>
          <Avatar className="h-8 w-8 sm:h-10 sm:w-10">
            <AvatarFallback className="text-sm">{selectedPartnerProfile?.display_name?.[0] || 'U'}</AvatarFallback>
          </Avatar>
          <CardTitle className="text-base sm:text-lg">{selectedPartnerProfile?.display_name || 'User'}</CardTitle>
        </div>
      </CardHeader>
      <ScrollArea className="flex-1 p-3 sm:p-4">
        <div className="space-y-3 sm:space-y-4">
          {messages?.map((msg) => {
            const isMe = msg.sender_id === user?.id;
            return (
              <div key={msg.id} className={`flex ${isMe ? 'justify-end' : 'justify-start'}`}>
                <div className={`max-w-[80%] sm:max-w-[70%] rounded-lg p-2.5 sm:p-3 ${
                  isMe ? 'bg-primary text-primary-foreground' : 'bg-muted'
                }`}>
                  <p className="text-sm">{msg.message}</p>
                  <p className="text-[10px] sm:text-xs opacity-70 mt-1">
                    {formatDistanceToNow(new Date(msg.created_at), { addSuffix: true })}
                  </p>
                </div>
              </div>
            );
          })}
        </div>
      </ScrollArea>
      <CardContent className="border-t p-3 sm:p-4">
        <form
          onSubmit={(e) => {
            e.preventDefault();
            sendMessageMutation.mutate();
          }}
          className="flex gap-2"
        >
          <Input
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            placeholder="Type a message..."
            disabled={sendMessageMutation.isPending}
            className="text-sm"
          />
          <Button type="submit" size="icon" disabled={!message.trim() || sendMessageMutation.isPending} className="h-9 w-9 shrink-0">
            <Send className="h-4 w-4" />
          </Button>
        </form>
      </CardContent>
    </Card>
  );
}
